/**
 * Service Profile API Service
 *
 * Handles all API calls for ServiceProfiles and the ProfileWorkItem
 * picker list used during profile creation / editing.
 *
 * Follows the same axiosInstance pattern as jobApi.js.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Service Profile CRUD
// ============================================

/**
 * List service profiles for the current organisation (paginated).
 * @param {{ page?: number, size?: number, activeOnly?: boolean }} params
 */
export const getServiceProfilesApi = async ({ page = 0, size = 10, activeOnly = false } = {}) => {
    const response = await axiosInstance.get('/service-profiles', {
        params: { page, size, activeOnly },
    });
    return response.data;
};

/**
 * Get a single service profile by UUID.
 * @param {string} uuid
 */
export const getServiceProfileApi = async (uuid) => {
    const response = await axiosInstance.get(`/service-profiles/${uuid}`);
    return response.data;
};

/**
 * Create a new service profile.
 * @param {{ name: string, make?: string, model?: string, vehicleYear?: number, mileageKm?: number, workItemUuids: string[] }} data
 */
export const createServiceProfileApi = async (data) => {
    const response = await axiosInstance.post('/service-profiles', data);
    return response.data;
};

/**
 * Update an existing service profile.
 * Replaces the work items list entirely with the provided ordered UUIDs.
 * @param {string} uuid
 * @param {{ name: string, make?: string, model?: string, vehicleYear?: number, mileageKm?: number, workItemUuids: string[] }} data
 */
export const updateServiceProfileApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/service-profiles/${uuid}`, data);
    return response.data;
};

/**
 * Activate a service profile.
 * @param {string} uuid
 */
export const activateServiceProfileApi = async (uuid) => {
    const response = await axiosInstance.patch(`/service-profiles/${uuid}/activate`);
    return response.data;
};

/**
 * Deactivate a service profile.
 * @param {string} uuid
 */
export const deactivateServiceProfileApi = async (uuid) => {
    const response = await axiosInstance.patch(`/service-profiles/${uuid}/deactivate`);
    return response.data;
};

/**
 * Delete a service profile.
 * @param {string} uuid
 */
export const deleteServiceProfileApi = async (uuid) => {
    const response = await axiosInstance.delete(`/service-profiles/${uuid}`);
    return response.data;
};

// ============================================
// Approval Modes (enum lookup)
// ============================================

/**
 * Fetch the available ApprovalMode enum values.
 * @returns {Promise<string[]>} e.g. ['PROFILE', 'WORK_ITEM']
 */
export const getApprovalModesApi = async () => {
    const response = await axiosInstance.get('/service-profiles/approval-modes');
    return response.data;
};

// ============================================
// Profile Work Items (picker support)
// ============================================

/**
 * Fetch all active ProfileWorkItems for the work item picker.
 * Requests a large page to get the full list in one call.
 * @param {{ activeOnly?: boolean, size?: number }} params
 */
export const getProfileWorkItemsForPickerApi = async ({ activeOnly = true, size = 200 } = {}) => {
    const response = await axiosInstance.get('/profile-work-items', {
        params: { page: 0, size, activeOnly },
    });
    return response.data;
};

// ============================================
// Default Export
// ============================================

/**
 * Copies the bundled starter service profiles — and the work items they bundle — into
 * this workshop as ordinary editable rows.
 *
 * One-time: the server no-ops when the workshop already has profiles, and says so in
 * `alreadyProvisioned` rather than failing, so pressing it twice is harmless.
 *
 * @returns {{created: number, workItemsCreated: number, alreadyProvisioned: boolean}}
 */
export const provisionStarterServiceProfilesApi = async () => {
    const response = await axiosInstance.post('/service-profiles/provision-defaults');
    return response.data;
};

const serviceProfileApi = {
    provisionStarterServiceProfilesApi,
    getServiceProfilesApi,
    getServiceProfileApi,
    createServiceProfileApi,
    updateServiceProfileApi,
    activateServiceProfileApi,
    deactivateServiceProfileApi,
    deleteServiceProfileApi,
    getApprovalModesApi,
    getProfileWorkItemsForPickerApi,
};

export default serviceProfileApi;